import {
  Game,
  Schedule,
  UnifiedConfig,
} from "@aura-frontend/omnibus-az-client";
import {
  Button,
  cn,
  HorizontalMenu,
  Icons,
  useToast,
} from "@win-reality/react-ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useNavMenu } from "@/components/ui/SideMenu";
import { getHomeAwayLineups } from "@/pages/Tagging/utils/api-helpers";
import { formatHHMM } from "@/utils/dateUtils";
import { getEventTimes } from "@/utils/eventUtils";

import { EventConfiguration } from "./EventConfiguration";
import {
  type ConfigurationFormValues,
  type EventFormData,
  type LineupFormValues,
  type OverviewFormValues,
} from "./EventForm.schema";
import { Lineup } from "./Lineup/Lineup";
import { useTeamVisibilityGroup } from "./Lineup/useTeamVisibilityGroup";
import { LiveEventLockedConfiguration } from "./LiveEventLockedConfiguration/LiveEventLockedConfiguration";
import { LiveEventLockedLineup } from "./LiveEventLockedLineup/LiveEventLockedLineup";
import { type InitialOverviewValues, Overview } from "./Overview/Overview";
import { OverviewLive } from "./Overview/OverviewLive";

export type TeamRef = {
  uuid: string;
  name: string;
  visibilityGroup: string;
  parentName?: string | null;
};
export type GameTeams = { home: TeamRef; away: TeamRef };
const emptyTeamRef: TeamRef = {
  uuid: "",
  name: "",
  visibilityGroup: "",
  parentName: null,
};

const TABS = [
  { label: "Overview", key: "overview" },
  { label: "Configurations", key: "configuration" },
  { label: "Lineup", key: "lineup" },
] as const;

type EventFormProps = {
  fieldId: string;
  fieldName: string;
  isFieldHealthy: boolean;
  complexName: string;
  onSubmit: (data: EventFormData) => void;
  variant: "edit" | "create";
  onDirtyChange?: (isDirty: boolean) => void;
  isSubmitting?: boolean;
  currentEvent?: Game;
  initialEventData?: Schedule;
  excludeScheduleId?: string;
  onLiveEndTimeUpdate?: (endTime: string) => Promise<void>;
  onEndEvent?: () => void | Promise<void>;
  initialOverviewValues?: InitialOverviewValues;
  isLive?: boolean;
  onEventTypeChange?: (type: Game["type"] | undefined) => void;
  fieldUnifiedConfig?: UnifiedConfig;
};

function EventForm({
  onSubmit,
  variant,
  onDirtyChange,
  isSubmitting,
  fieldId,
  fieldName,
  isFieldHealthy,
  complexName,
  currentEvent,
  initialEventData,
  excludeScheduleId,
  onLiveEndTimeUpdate,
  onEndEvent,
  initialOverviewValues,
  isLive,
  onEventTypeChange,
  fieldUnifiedConfig,
}: EventFormProps) {
  const [tab, setTab] = useState<"overview" | "configuration" | "lineup">(
    "overview",
  );
  const [visitedSteps, setVisitedSteps] = useState<Set<string>>(
    () => new Set(["overview"]),
  );
  const goToTab = useCallback(
    (key: "overview" | "configuration" | "lineup") => {
      setTab(key);
      setVisitedSteps((prev) =>
        prev.has(key) ? prev : new Set(prev).add(key),
      );
    },
    [],
  );
  const navigate = useNavigate();
  const { isCollapsed } = useNavMenu();
  const { toast } = useToast();

  const [eventType, setEventTypeInternal] = useState<Game["type"] | undefined>(
    undefined,
  );

  const setEventType = useCallback(
    (type: Game["type"] | undefined) => {
      setEventTypeInternal(type);
      onEventTypeChange?.(type);
    },
    [onEventTypeChange],
  );

  const [gameTeams, setGameTeams] = useState<GameTeams>(() => {
    const initialGame = initialEventData?.merged?.game;
    if (initialGame?.type === "game") {
      const displayName = initialGame.displayName ?? "";
      // Older games have no teamNames; their names live in "Home x Away"
      const parts = displayName.split(" x ");
      const homeName = initialGame.teamNames?.home || parts[0]?.trim() || "";
      const awayName = initialGame.teamNames?.away || parts[1]?.trim() || "";
      return {
        home: {
          uuid: initialGame.teams?.home ?? "",
          name: homeName,
          visibilityGroup: "",
          parentName: null,
        },
        away: {
          uuid: initialGame.teams?.away ?? "",
          name: awayName,
          visibilityGroup: "",
          parentName: null,
        },
      };
    }
    if (initialGame) {
      return {
        home: {
          uuid: initialGame.teams?.home ?? "",
          name: initialGame.displayName ?? "",
          visibilityGroup: "",
          parentName: null,
        },
        away: emptyTeamRef,
      };
    }
    return { home: emptyTeamRef, away: emptyTeamRef };
  });

  const handleTeamChange = useCallback(
    (side: "home" | "away", ref: TeamRef) => {
      setGameTeams((prev) => {
        const current = prev[side];
        if (
          current.uuid === ref.uuid &&
          current.name === ref.name &&
          current.visibilityGroup === ref.visibilityGroup &&
          (current.parentName ?? null) === (ref.parentName ?? null)
        ) {
          return prev;
        }
        return { ...prev, [side]: ref };
      });
    },
    [],
  );

  const { data: resolvedHome } = useTeamVisibilityGroup(
    !gameTeams.home.visibilityGroup ? gameTeams.home.uuid : undefined,
  );
  const { data: resolvedAway } = useTeamVisibilityGroup(
    !gameTeams.away.visibilityGroup ? gameTeams.away.uuid : undefined,
  );

  useEffect(() => {
    if (resolvedHome) {
      setGameTeams((prev) =>
        prev.home.visibilityGroup === resolvedHome.visibilityGroup &&
        (prev.home.parentName ?? null) === resolvedHome.parentName
          ? prev
          : {
              ...prev,
              home: {
                ...prev.home,
                visibilityGroup: resolvedHome.visibilityGroup,
                parentName: resolvedHome.parentName,
              },
            },
      );
    }
  }, [resolvedHome]);

  useEffect(() => {
    if (resolvedAway) {
      setGameTeams((prev) =>
        prev.away.visibilityGroup === resolvedAway.visibilityGroup &&
        (prev.away.parentName ?? null) === resolvedAway.parentName
          ? prev
          : {
              ...prev,
              away: {
                ...prev.away,
                visibilityGroup: resolvedAway.visibilityGroup,
                parentName: resolvedAway.parentName,
              },
            },
      );
    }
  }, [resolvedAway]);

  type EventFormValidity = {
    overview: boolean;
    configuration: boolean;
    lineup: boolean;
  };

  const [validity, setValidity] = useState<EventFormValidity>({
    overview: false,
    configuration: false,
    lineup: false,
  });

  const setOverviewValid = useCallback(
    (isValid: boolean) =>
      setValidity((prev) => ({ ...prev, overview: isValid })),
    [],
  );
  const setConfigurationValid = useCallback(
    (isValid: boolean) =>
      setValidity((prev) => ({ ...prev, configuration: isValid })),
    [],
  );
  const setLineupValid = useCallback(
    (isValid: boolean) => setValidity((prev) => ({ ...prev, lineup: isValid })),
    [],
  );

  const allValid = validity.overview && validity.configuration;

  type EventFormDirty = {
    overview: boolean;
    configuration: boolean;
    lineup: boolean;
  };

  const [dirty, setDirty] = useState<EventFormDirty>({
    overview: false,
    configuration: false,
    lineup: false,
  });

  const setOverviewDirty = useCallback(
    (isDirty: boolean) => setDirty((prev) => ({ ...prev, overview: isDirty })),
    [],
  );
  const setConfigurationDirty = useCallback(
    (isDirty: boolean) =>
      setDirty((prev) => ({ ...prev, configuration: isDirty })),
    [],
  );
  const setLineupDirty = useCallback(
    (isDirty: boolean) => setDirty((prev) => ({ ...prev, lineup: isDirty })),
    [],
  );

  const isAnyDirty = dirty.overview || dirty.configuration || dirty.lineup;

  useEffect(() => {
    onDirtyChange?.(isAnyDirty);
  }, [isAnyDirty, onDirtyChange]);

  const overviewValues = useRef<OverviewFormValues | null>(null);
  const configurationValues = useRef<ConfigurationFormValues | null>(null);
  const lineupValues = useRef<LineupFormValues | null>(null);
  const practiceTeamVisibilityGroup = useRef<string>("");

  const handleOverviewValues = useCallback((values: OverviewFormValues) => {
    overviewValues.current = values;
  }, []);

  const handleConfigurationValues = useCallback(
    (values: ConfigurationFormValues) => {
      configurationValues.current = values;
    },
    [],
  );

  // Null once the lineup stops validating, so a stale lineup isn't saved
  const handleLineupValues = useCallback((values: LineupFormValues | null) => {
    lineupValues.current = values;
  }, []);

  const handleSubmit = () => {
    if (!overviewValues.current || !configurationValues.current) return;

    const overviewData = overviewValues.current;
    let teamColors: { home?: string; away?: string } | undefined;

    if (
      eventType === "game" &&
      "homeTeamColor" in overviewData &&
      "awayTeamColor" in overviewData
    ) {
      teamColors = {
        home: `#${overviewData.homeTeamColor}`,
        away: `#${overviewData.awayTeamColor}`,
      };
    } else if (
      eventType === "batting_practice" &&
      "teamColor" in overviewData
    ) {
      teamColors = { home: `#${overviewData.teamColor}` };
    }

    const data: EventFormData = {
      overview: overviewValues.current,
      configuration: configurationValues.current,
      ...(eventType === "game" && lineupValues.current
        ? { lineup: lineupValues.current }
        : {}),
      ...(eventType === "game"
        ? {
            gameTeams: {
              home: { uuid: gameTeams.home.uuid, name: gameTeams.home.name },
              away: { uuid: gameTeams.away.uuid, name: gameTeams.away.name },
            },
          }
        : {}),
      ...(eventType !== "game" && practiceTeamVisibilityGroup.current
        ? { practiceTeamVisibilityGroup: practiceTeamVisibilityGroup.current }
        : {}),
      ...(teamColors ? { teamColors } : {}),
    };

    onSubmit(data);
  };

  const tabs = useMemo(() => {
    if (
      eventType === "batting_practice" ||
      eventType === "bullpen" ||
      !eventType
    ) {
      return TABS.filter((t) => t.key !== "lineup");
    }

    return TABS;
  }, [eventType]);

  useEffect(() => {
    if (!tabs.some((t) => t.key === tab)) setTab(tabs[0].key);
  }, [tabs, tab]);

  const stepValidity: Record<(typeof TABS)[number]["key"], boolean> = {
    overview: validity.overview,
    configuration: validity.configuration,
    lineup: validity.lineup,
  };

  const currentStepIndex = Math.max(
    0,
    tabs.findIndex((t) => t.key === tab),
  );
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === tabs.length - 1;

  const handleSubmitClick = () => {
    if (!allValid) {
      // The lineup is optional, so it's never what's holding up the submit
      const incomplete = tabs
        .map((t, i) => ({ t, i }))
        .filter(({ t }) => t.key !== "lineup" && !stepValidity[t.key])
        .map(({ t, i }) => `Step #${i + 1}: ${t.label}`);
      toast(`Please complete the following steps: ${incomplete.join(", ")}.`, {
        variant: "info",
      });
      return;
    }
    handleSubmit();
  };

  const isEditing = !!currentEvent && variant === "edit";

  const [isLiveSubmitting, setIsLiveSubmitting] = useState(false);

  const handleLiveEndTimeUpdate = useCallback(
    async (endTime: string) => {
      if (!onLiveEndTimeUpdate) return;
      setIsLiveSubmitting(true);
      try {
        await onLiveEndTimeUpdate(endTime);
      } finally {
        setIsLiveSubmitting(false);
      }
    },
    [onLiveEndTimeUpdate],
  );

  const scheduleGame: Game | undefined =
    initialEventData?.merged?.game ?? undefined;

  const schedulePark = initialEventData?.merged?.park;
  const scheduleLed = initialEventData?.merged?.led;
  const scheduleName = initialEventData?.merged?.name;
  // `notes` is the one display field not present on `ScheduleMerged`, so it
  // still has to be sourced from internal/external until the backend adds it.
  const scheduleNotes =
    initialEventData?.internal?.notes ?? initialEventData?.external?.notes;

  const derivedInitialOverviewValues: InitialOverviewValues | undefined =
    useMemo(() => {
      if (!initialEventData || !scheduleGame) return undefined;
      const { startTime, endTime } = getEventTimes(scheduleGame);
      return {
        startDate: new Date(startTime),
        startTime: formatHHMM(startTime),
        endTime: endTime ? formatHHMM(endTime) : undefined,
        orgId: initialEventData.internal?.org_id ?? undefined,
      };
    }, [initialEventData, scheduleGame]);
  const configInitialValues: Partial<ConfigurationFormValues> | undefined =
    initialEventData
      ? {
          // Use `!== undefined` rather than a truthy check so an explicit
          // `led: null` (Standby) round-trips through prefill
          // instead of being replaced with the form default.
          ...(scheduleLed !== undefined && { ledBoardMode: scheduleLed }),
          ...(schedulePark?.sport && {
            sport: schedulePark.sport as ConfigurationFormValues["sport"],
          }),
          // Backend returns `age_group` (snake_case) on schedule.park.field
          // even though the OpenAPI spec / generated type says `ageGroup`.
          // Read both so prefill works regardless of which side is updated.
          ...((schedulePark?.field?.ageGroup ??
            (schedulePark?.field as { age_group?: string } | undefined)
              ?.age_group) && {
            level: (schedulePark?.field?.ageGroup ??
              (schedulePark?.field as { age_group?: string } | undefined)
                ?.age_group) as ConfigurationFormValues["level"],
          }),
          ...(schedulePark?.rubberDistanceF != null && {
            moundDistance: schedulePark.rubberDistanceF,
          }),
          ...(schedulePark?.field?.diamondSizeFeet != null && {
            baseDistance: schedulePark.field.diamondSizeFeet,
          }),
          ...(schedulePark?.field?.infieldAtFoulLineFeet != null && {
            infieldAtFoulLine: schedulePark.field.infieldAtFoulLineFeet,
          }),
          ...(schedulePark?.field?.fenceFirstBaseFeet != null && {
            fenceFirstBase: schedulePark.field.fenceFirstBaseFeet,
          }),
          ...(schedulePark?.field?.fenceThirdBaseFeet != null && {
            fenceThirdBase: schedulePark.field.fenceThirdBaseFeet,
          }),
          ...(schedulePark?.field?.fenceLeftFieldFeet != null && {
            leftField: schedulePark.field.fenceLeftFieldFeet,
          }),
          ...(schedulePark?.field?.fenceCenterFieldFeet != null && {
            centerField: schedulePark.field.fenceCenterFieldFeet,
          }),
          ...(schedulePark?.field?.fenceRightFieldFeet != null && {
            rightField: schedulePark.field.fenceRightFieldFeet,
          }),
          ...(schedulePark?.strikeZone && {
            strikeZone: schedulePark.strikeZone,
          }),
        }
      : fieldUnifiedConfig
        ? {
            ...(fieldUnifiedConfig.sport && {
              sport:
                fieldUnifiedConfig.sport as ConfigurationFormValues["sport"],
            }),
            ...(fieldUnifiedConfig.field?.ageGroup && {
              level: fieldUnifiedConfig.field
                .ageGroup as ConfigurationFormValues["level"],
            }),
            ...(fieldUnifiedConfig.rubberDistanceF != null && {
              moundDistance: fieldUnifiedConfig.rubberDistanceF,
            }),
            ...(fieldUnifiedConfig.field?.diamondSizeFeet != null && {
              baseDistance: fieldUnifiedConfig.field.diamondSizeFeet,
            }),
            ...(fieldUnifiedConfig.field?.infieldAtFoulLineFeet != null && {
              infieldAtFoulLine: fieldUnifiedConfig.field.infieldAtFoulLineFeet,
            }),
            ...(fieldUnifiedConfig.field?.fenceFirstBaseFeet != null && {
              fenceFirstBase: fieldUnifiedConfig.field.fenceFirstBaseFeet,
            }),
            ...(fieldUnifiedConfig.field?.fenceThirdBaseFeet != null && {
              fenceThirdBase: fieldUnifiedConfig.field.fenceThirdBaseFeet,
            }),
            ...(fieldUnifiedConfig.field?.fenceLeftFieldFeet != null && {
              leftField: fieldUnifiedConfig.field.fenceLeftFieldFeet,
            }),
            ...(fieldUnifiedConfig.field?.fenceCenterFieldFeet != null && {
              centerField: fieldUnifiedConfig.field.fenceCenterFieldFeet,
            }),
            ...(fieldUnifiedConfig.field?.fenceRightFieldFeet != null && {
              rightField: fieldUnifiedConfig.field.fenceRightFieldFeet,
            }),
            ...(fieldUnifiedConfig.strikeZone && {
              strikeZone: fieldUnifiedConfig.strikeZone,
            }),
          }
        : undefined;

  return (
    <div
      className={cn(
        "flex flex-col grow",
        tab === "lineup" ? "h-full" : "min-h-full",
      )}
    >
      <div className={cn("flex flex-col", tab === "lineup" && "flex-1")}>
        <div className="sticky top-18 z-10 bg-white flex flex-col justify-center min-w-0">
          <HorizontalMenu
            size="md"
            wrapperClassName="w-full [&>*]:mx-auto"
            items={tabs.map((t) => ({
              label: t.label,
              active: tab === t.key,
              onClick: () => goToTab(t.key),
            }))}
          />

          <div className="h-px mx-6 bg-gray-200"></div>
        </div>

        <div
          className={cn(
            "mx-auto px-5 pt-6 w-full",
            tab !== "lineup" && "max-w-130 pb-54",
            tab === "lineup" &&
              "w-full max-w-300 flex-1 overflow-hidden pb-54 flex flex-col",
          )}
        >
          <div className={cn(tab !== "overview" && "hidden")}>
            {isEditing && currentEvent ? (
              <OverviewLive
                onValidChange={setOverviewValid}
                fieldId={fieldId}
                event={currentEvent}
                initialName={scheduleName}
                initialNotes={scheduleNotes}
                excludeScheduleId={excludeScheduleId}
                onEndTimeUpdate={handleLiveEndTimeUpdate}
                onEndEvent={onEndEvent}
                isUpdating={isLiveSubmitting}
              />
            ) : (
              <Overview
                onEventTypeChange={setEventType}
                onValidChange={setOverviewValid}
                onDirtyChange={setOverviewDirty}
                onValuesChange={handleOverviewValues}
                onTeamChange={handleTeamChange}
                onPracticeTeamVisibilityGroupChange={(vg) => {
                  practiceTeamVisibilityGroup.current = vg;
                }}
                gameTeams={gameTeams}
                fieldId={fieldId}
                fieldName={fieldName}
                event={scheduleGame}
                initialName={scheduleName}
                initialNotes={scheduleNotes}
                onOpenLineupTab={() => goToTab("lineup")}
                excludeScheduleId={excludeScheduleId}
                initialOverviewValues={
                  derivedInitialOverviewValues ?? initialOverviewValues
                }
                isLive={isLive}
              />
            )}
          </div>
          <div className={cn(tab !== "configuration" && "hidden")}>
            {isEditing && currentEvent ? (
              <LiveEventLockedConfiguration
                complexName={complexName}
                fieldId={fieldId}
              />
            ) : (
              <EventConfiguration
                isFieldHealthy={isFieldHealthy}
                fieldName={fieldName}
                onValidChange={setConfigurationValid}
                onDirtyChange={setConfigurationDirty}
                onValuesChange={handleConfigurationValues}
                initialValues={configInitialValues}
                isLive={isLive}
                isEditing={variant === "edit"}
              />
            )}
          </div>
          <div
            className={cn(
              tab !== "lineup" && "hidden",
              tab === "lineup" && "flex flex-col flex-1",
            )}
          >
            {isEditing && currentEvent ? (
              <LiveEventLockedLineup />
            ) : (
              <Lineup
                gameTeams={gameTeams}
                initialLineups={
                  scheduleGame?.type === "game"
                    ? getHomeAwayLineups(scheduleGame)
                    : undefined
                }
                onTeamChange={handleTeamChange}
                onValidChange={setLineupValid}
                onDirtyChange={setLineupDirty}
                onValuesChange={handleLineupValues}
                isEditing={variant === "edit"}
              />
            )}
          </div>
        </div>
      </div>

      {variant === "edit" ? (
        <div
          className={cn(
            "fixed bottom-0 left-0 flex w-full items-center justify-center bg-white py-4 shadow-scroller-top",
            !isCollapsed ? "md:pl-64" : "md:pl-18",
          )}
        >
          <div className="mx-auto flex w-130 px-5 items-center justify-between">
            <Button
              variant="ghost"
              className="relative right-6 underline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>

            <Button
              variant="secondary-flat-light"
              isDisabled={!allValid}
              onClick={handleSubmit}
              isLoading={isSubmitting}
            >
              Save Event
            </Button>
          </div>
        </div>
      ) : (
        <div
          id="new-event-form-controls-portal"
          className={cn(
            "@container fixed bottom-0 left-0 flex w-full flex-col items-center bg-white shadow-scroller-top pt-16 pb-4",
            !isCollapsed ? "md:pl-64" : "md:pl-18",
          )}
        >
          <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-2 px-3 @xl:gap-4 @xl:px-5">
            <Button
              variant="secondary-flat-light-outline"
              isDisabled={isFirstStep}
              onClick={() => goToTab(tabs[currentStepIndex - 1].key)}
              className="pl-3 w-26"
              size="sm"
            >
              <Icons.ChevronLeft className="size-4" />
              <span className="hidden @xs:inline">Back</span>
            </Button>

            <ol className="flex min-w-0 items-center gap-1.5 @xl:gap-3">
              {tabs.map((t, i) => {
                const valid = stepValidity[t.key] && visitedSteps.has(t.key);
                return (
                  <li
                    key={t.key}
                    className="flex items-center gap-1.5 @xl:gap-3"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                          valid
                            ? "bg-green-500 text-white"
                            : "bg-black text-white",
                        )}
                      >
                        {valid ? (
                          <Icons.CheckMicro className="size-3.5" />
                        ) : (
                          i + 1
                        )}
                      </span>
                      <div className="hidden flex-col text-xs leading-tight @xl:flex">
                        <span>Step #{i + 1}:</span>
                        <span>{t.label}</span>
                      </div>
                    </div>
                    {i < tabs.length - 1 && (
                      <span
                        className="h-px w-1  md:w-3 bg-gray-300 @xl:w-4"
                        aria-hidden
                      />
                    )}
                  </li>
                );
              })}
            </ol>

            {isLastStep ? (
              <Button
                variant="secondary-flat-light"
                onClick={handleSubmitClick}
                isLoading={isSubmitting}
                size="sm"
                className="w-28 md:w-30"
              >
                {isLive
                  ? eventType === "game"
                    ? "Start Game"
                    : "Start Practice"
                  : "Create Event"}
              </Button>
            ) : (
              <Button
                variant="secondary-flat-light"
                size="sm"
                className="w-28"
                onClick={() => goToTab(tabs[currentStepIndex + 1].key)}
              >
                Next
              </Button>
            )}
          </div>

          <Button
            variant="ghost"
            className="underline mt-6"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

export { EventForm };
